import React from "react";
import { CgSpinnerTwo } from "react-icons/cg";
import { MdPersonRemoveAlt1 } from "react-icons/md";

import { ActionEyeFilledIcon } from "@/Components/Icons/ActionEyeFilledIcon";
import { ActionEyeIcon } from "@/Components/Icons/ActionEyeIcon";
import AppliedCalendarFilled from "@/Components/Icons/AppliedCalendarFilled";
import AppliedCalender from "@/Components/Icons/AppliedCalender";
import { ApproveFilledIcon } from "@/Components/Icons/ApproveFilledIcon";
import { ApproveIcon } from "@/Components/Icons/ApproveIcon";
import { CalendarIcon } from "@/Components/Icons/CalendarIcon";
import ContactPrimaryIcon from "@/Components/Icons/ContactPrimaryIcon";
import ContractIcon from "@/Components/Icons/ContractIcon";
import DeleteIcon from "@/Components/Icons/DeleteIcon";
import { DownloadFilledIcon } from "@/Components/Icons/DownloadFilledIcon";
import { DownloadIcon } from "@/Components/Icons/DownloadIcon";
import { EditCircleFilledIcon } from "@/Components/Icons/EditCircleFilledIcon";
import { EditCircleIcon } from "@/Components/Icons/EditCircleIcon";
import ExportFilledIcon from "@/Components/Icons/ExportFilledIcon";
import ExportIcon from "@/Components/Icons/ExportIcon";
import MergeBookedCandidateContracts from "@/Components/Icons/MergeBookedCandidateContracts";
import MinusFilledIcon from "@/Components/Icons/MinusFilledIcon";
import MinusIcon from "@/Components/Icons/MinusIcon";
import { PencilIcon } from "@/Components/Icons/PencilIcon";
import PrintFilledIcon from "@/Components/Icons/PrintFilledIcon";
import PrintIcon from "@/Components/Icons/PrintIcon";
import { RefreshIcon } from "@/Components/Icons/RefreshIcon";
import { RejectFilledIcon } from "@/Components/Icons/RejectFilledIcon";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import { SendInviteFilledIcon } from "@/Components/Icons/SendInviteFilledIcon";
import { SendInviteIcon } from "@/Components/Icons/SendInviteIcon";
import ShiftIcon from "@/Components/Icons/ShiftIcon";
import { StarFilledIcon } from "@/Components/Icons/StarFilledIcon";
import { StarIcon } from "@/Components/Icons/StarIcon";
import { UnsuitableFilledIcon } from "@/Components/Icons/UnsuitableFilledIcon";
import { UnsuitableIcon } from "@/Components/Icons/UnsuitableIcon";

import AddIcon from "../../Icons/AddIcon";
import ArchiveFilledIcon from "../../Icons/ArchiveFilledIcon";
import ArchiveIcon from "../../Icons/ArchiveIcon";
import AutoApproveBankCandiateFilledIcon from "../../Icons/AutoApproveBankCandiateFilledIcon";
import AutoApproveBankCandidateIcon from "../../Icons/AutoApproveBankCandidateIcon";
import CandidateAutoApproveBankCandidateIcon from "../../Icons/CandidateAutoApproveBankCandidateIcon";
import CandidateComplianceOverRIdeIcon from "../../Icons/CandidateComplianceOverRIdeIcon";
import CandidateMarkAsUnsuitableIcon from "../../Icons/CandidateMarkAsUnsuitableIcon";
import ChangeCandidatePayIcon from "../../Icons/ChangeCandidatePayIcon";
import { CheckIcon } from "../../Icons/CheckIcon";
import CloseIcon from "../../Icons/CloseIcon";
import ContractPublishToAppLocumFilledIcon from "../../Icons/ContractPublishToAppLocumFilledIcon";
import ContractPublishToAppLocumIcon from "../../Icons/ContractPublishToAppLocumIcon";
import { EnvelopeIcon } from "../../Icons/EnvelopeIcon";
import OverrideComplianceOfBank from "../../Icons/OverrideComplianceOfBank";
import OverrideComplianceOfBankFilled from "../../Icons/OverrideComplianceOfBankFilled";
import RemoveAutoApproveBankCandiate from "../../Icons/RemoveAutoApproveBankCandiate";
import RemoveAutoApproveBankCandidateFilledIcon from "../../Icons/RemoveAutoApproveBankCandidateFilledIcon";
import { TimePickerClock } from "../../Icons/TimePickerClock";

/**
 * Renders action icons based on the type and loading status.
 *
 * @param {boolean} isLoading - Indicates whether the icons should be in loading state
 * @param {string} type - The type of action icons to render
 * @return {ReactNode} The rendered action icons based on the type and loading status
 */
const ActionIcons = ({ isLoading, type }) => {
  const loadingColor = {
    shortlist: "text-yellow",
    unsuitable: "text-red",
    invite: "text-primary",
    approve: "text-green",
    reject: "text-red",
    edit: "text-txtColor",
    appliedCalender: "text-primary",
    mergeContract: "text-green",
    autoApproveBankCandidate: "text-green",
    RemoveAutoApproveBankCandiate: "text-red",
    add: "text-green",
    export: "text-primary",
    archive: "text-red",
  };
  const data = {
    shortlist: (
      <>
        <StarIcon cls="group-hover:hidden" />
        <StarFilledIcon cls="hidden text-yellow group-hover:block" />
      </>
    ),
    unsuitable: (
      <>
        <UnsuitableIcon cls="group-hover:hidden" />
        <UnsuitableFilledIcon cls="hidden text-red group-hover:block" />
      </>
    ),
    invite: (
      <>
        <SendInviteIcon cls="group-hover:hidden" />
        <SendInviteFilledIcon cls="hidden text-primary group-hover:block" />
      </>
    ),
    approve: (
      <>
        <ApproveIcon cls="group-hover:hidden" />
        <ApproveFilledIcon cls="hidden text-green group-hover:block" />
      </>
    ),
    reject: (
      <>
        <RejectIcon cls="group-hover:hidden" />
        <RejectFilledIcon cls="hidden text-red group-hover:block" />
      </>
    ),
    add: (
      <>
        <AddIcon cls="w-[18px] h-[18px] hover:text-green group-hover:text-green" />
      </>
    ),
    edit: (
      <>
        <EditCircleIcon cls="group-hover:hidden" />
        <EditCircleFilledIcon cls="hidden text-primary group-hover:block" />
      </>
    ),
    download: (
      <>
        <DownloadIcon cls="group-hover:hidden" />
        <DownloadFilledIcon cls="hidden text-primary group-hover:block" />
      </>
    ),
    eye: (
      <>
        <ActionEyeIcon cls="group-hover:hidden" />
        <ActionEyeFilledIcon cls="hidden text-primary group-hover:block" />
      </>
    ),
    delete: (
      <>
        <DeleteIcon cls="group-hover:text-red" />
      </>
    ),
    contactPrimary: (
      <>
        <ContactPrimaryIcon cls={"group-hover:text-[#5EBB5B] !w-[27px]"} />
      </>
    ),
    refresh: (
      <>
        <RefreshIcon />
      </>
    ),
    editPencil: (
      <>
        <PencilIcon cls="w-[18px] h-[18px]" />
      </>
    ),
    minus: (
      <>
        <MinusIcon cls="group-hover:hidden" />
        <MinusFilledIcon cls="hidden text-[#343638] group-hover:block" />
      </>
    ),
    print: (
      <>
        <PrintIcon cls="w-[18px] h-[18px] group-hover:hidden" />
        <PrintFilledIcon cls="w-[18px] h-[18px] hidden text-green group-hover:block" />
      </>
    ),
    export: (
      <>
        <ExportIcon cls="w-[24px] h-[24px] group-hover:hidden" />
        <ExportFilledIcon cls="w-[24px] h-[24px] hidden text-primary group-hover:block" />
      </>
    ),
    shifts: (
      <>
        <ShiftIcon cls="w-[18px] h-[18px]" />
      </>
    ),
    contracts: (
      <>
        <ContractIcon cls="w-[18px] h-[18px]" />
      </>
    ),
    appliedCalender: (
      <>
        <AppliedCalender cls="w-[26px] h-[26px] group-hover:hidden -mt-[3px]" />
        <AppliedCalendarFilled cls="w-[24px] h-[24px] hidden text-primary group-hover:block -mt-[1px]" />
      </>
    ),
    viewCalender: (
      <>
        <CalendarIcon />
      </>
    ),
    mergeContract: (
      <>
        <MergeBookedCandidateContracts cls="w-[20px] h-[20px] text-txtColor group-hover:text-green" />
      </>
    ),
    contractPublishToAppLocum: (
      <>
        <ContractPublishToAppLocumIcon cls="w-[28px] h-[28px] text-txtColor group-hover:hidden" />
        <ContractPublishToAppLocumFilledIcon cls="w-[28px] h-[28px] hidden text-green group-hover:block" />
      </>
    ),
    setNewDurationForBankContracts: (
      <>
        <TimePickerClock cls="w-[22px] h-[22px] text-txtColor group-hover:text-blue" />
        {/* <SetNewDurationForBankedContractIconFilled cls="w-[22px] h-[22px] hidden text-green group-hover:block" /> */}
      </>
    ),
    updateCandidatePayStatus: (
      <>
        <ChangeCandidatePayIcon cls="w-[25px] h-[25px] text-txtColor group-hover:text-blue" />
      </>
    ),
    resendComplianceToCandiate: (
      <>
        <EnvelopeIcon cls="w-[25px] h-[25px] text-txtColor group-hover:text-blue" />
      </>
    ),

    autoApproveBankCandidate: (
      <>
        <AutoApproveBankCandidateIcon cls="w-[19px] h-[19px] text-txtColor group-hover:hidden" />
        <AutoApproveBankCandiateFilledIcon cls="w-[19px] h-[19px] hidden text-green group-hover:block" />
      </>
    ),
    removeApproveBankCandidate: (
      <>
        <RemoveAutoApproveBankCandiate cls="w-[19px] h-[19px] text-txtColor group-hover:hidden" />
        <RemoveAutoApproveBankCandidateFilledIcon cls="w-[19px] h-[19px] hidden text-red group-hover:block" />
      </>
    ),
    overrideComplianceOfBankCandidate: (
      <>
        <OverrideComplianceOfBank cls="w-[22px] h-[22px] text-txtColor group-hover:hidden" />
        <OverrideComplianceOfBankFilled cls="w-[22px] h-[22px] hidden text-green  group-hover:block" />
      </>
    ),
    archive: (
      <>
        <ArchiveIcon cls="w-[22px] h-[22px] group-hover:hidden" />
        <ArchiveFilledIcon cls="w-[24px] h-[24px] hidden text-primary group-hover:block" />
      </>
    ),
    checkIn: <CheckIcon cls="h-[20px] w-[20px] group-hover:text-green" />,
    close: <CloseIcon cls="h-[24px] w-[24px] group-hover:text-red" />,
    removeCandidateFromBroadcast: (
      <MdPersonRemoveAlt1 className="h-[22px] w-[22px] hover:text-red group-hover:text-red" />
    ),
    candidateAutoApprove: (
      <CandidateAutoApproveBankCandidateIcon cls="w-[23px] h-[23px] !text-txtColor group-hover:text-blue" />
    ),
    candidateMarkAsUnsuitable: (
      <CandidateMarkAsUnsuitableIcon cls="w-[23px] h-[23px] !text-txtColor group-hover:text-blue" />
    ),
    candidateComplianceOverRide: (
      <CandidateComplianceOverRIdeIcon cls="w-[23px] h-[23px] !text-txtColor group-hover:text-blue" />
    ),
    candidateMarkAsPreffered: (
      <StarIcon cls="group-hover:hidden text-txtColor" />
    ),
  };
  return (
    <>
      {isLoading ? (
        <CgSpinnerTwo className={`animate-spin ${loadingColor[type]}`} />
      ) : (
        data[type]
      )}
    </>
  );
};

export default ActionIcons;
